<template>
  <div class="account-settings-panel" v-loading="loading">
    <el-card class="workspace-card toolbar-card" shadow="hover">
      <div class="page-toolbar">
        <div>
          <div class="toolbar-title">马自达账号 / Cookie / GGP 设置</div>
          <div class="toolbar-desc">查看和修改马自达账号密码、GGP 版本和在线 Cookie。</div>
        </div>
        <el-button :loading="loading" @click="loadInfo">刷新</el-button>
      </div>
    </el-card>

    <div class="settings-grid">
      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>马自达账号密码查询修改</span>
          </div>
        </template>

        <el-form label-position="top" class="setting-form" autocomplete="off">
          <!-- 阻止浏览器把网站登录账号/密码自动填到 账号密码管理输入框 -->
          <div class="autofill-guard" aria-hidden="true">
            <input type="text" name="username" autocomplete="username" tabindex="-1" />
            <input type="password" name="password" autocomplete="current-password" tabindex="-1" />
          </div>

          <el-form-item label="账号">
            <el-input
              v-model="accountForm.number"
              :name="`mazda-redis-number-${autofillSeed}`"
              autocomplete="off"
              :readonly="accountInputReadonly"
              placeholder="请输入马自达账号"
              clearable
              @focus="unlockAccountInputs"
            />
          </el-form-item>
          <el-form-item label="密码">
            <el-input
              v-model="accountForm.cipher"
              type="text"
              :name="`mazda-redis-cipher-${autofillSeed}`"
              autocomplete="off"
              :readonly="accountInputReadonly"
              placeholder="请输入马自达密码"
              clearable
              @focus="unlockAccountInputs"
            />
          </el-form-item>
          <div class="form-actions">
            <el-button type="primary" :loading="saving" @click="saveAccount">保存账号密码</el-button>
            <el-button @click="loadInfo">重新读取</el-button>
          </div>
        </el-form>
      </el-card>

      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>GGP 版本查看修改</span>
          </div>
        </template>

        <el-form label-position="top" class="setting-form">
          <el-form-item label="GGP 版本">
            <el-input v-model="ggpForm.version" placeholder="0x + 8 位十六进制" maxlength="10" clearable>
              <template #append>
                <el-button :loading="saving" @click="saveGgpVersion">保存</el-button>
              </template>
            </el-input>
          </el-form-item>
          <div class="field-tip">格式固定为 <strong>0x + 8 位十六进制</strong>。</div>
        </el-form>
      </el-card>
    </div>

    <el-card class="workspace-card cookie-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>当前在线 Cookie 数据</span>
          <div class="header-actions">
            <el-button type="success" :loading="refreshingCookie" :disabled="saving" @click="refreshCookie">
              更新 Cookie
            </el-button>
            <el-button type="primary" :loading="saving" :disabled="refreshingCookie" @click="saveCookie">
              保存 Cookie
            </el-button>
          </div>
        </div>
      </template>

      <el-alert
        title="这里直接查看和修改 Mazda 在线 cookie。为空时，后续在线安全校验会重新获取 cookie。"
        type="info"
        :closable="false"
        class="cookie-alert"
      />

      <div class="cookie-key-row">
        <strong>gxmAuthorisationToken</strong>
        <span>{{ tokenLengthText }}</span>
      </div>
      <el-input
        v-model="cookieForm.gxmAuthorisationToken"
        type="textarea"
        :autosize="{ minRows: 5, maxRows: 10 }"
        placeholder="gxmAuthorisationToken"
      />

      <div class="cookie-key-row second">
        <strong>gxmAuthorisationIdToken</strong>
        <span>{{ idTokenLengthText }}</span>
      </div>
      <el-input
        v-model="cookieForm.gxmAuthorisationIdToken"
        type="textarea"
        :autosize="{ minRows: 5, maxRows: 10 }"
        placeholder="gxmAuthorisationIdToken"
      />
    </el-card>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useMazdaAccountSettings } from '@/composables/mazda/useMazdaAccountSettings';

const props = defineProps({
  mazdaPost: { type: Function, required: true },
  serverName: { type: String, default: '' },
});

const accountInputReadonly = ref(true);
const autofillSeed = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const unlockAccountInputs = () => {
  accountInputReadonly.value = false;
};

const {
  loading,
  saving,
  accountForm,
  cookieForm,
  ggpForm,
  tokenLengthText,
  idTokenLengthText,
  refreshingCookie,
  loadInfo,
  saveAccount,
  saveCookie,
  refreshCookie,
  saveGgpVersion,
} = useMazdaAccountSettings(props.mazdaPost);
</script>

<style scoped>
.account-settings-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-title {
  font-weight: 800;
  color: #1f2d3d;
  font-size: 16px;
}

.toolbar-desc {
  margin-top: 6px;
  color: #7c8798;
  font-size: 13px;
}

.workspace-card {
  border-radius: 22px;
  border: 1px solid rgba(226, 233, 243, 0.95);
  overflow: hidden;
}

.card-header,
.header-actions,
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.card-header > span:first-child {
  font-weight: 800;
  color: #1f2d3d;
}

.summary-strip {
  display: grid;
  grid-template-columns: 180px minmax(260px, 1fr) minmax(260px, 1fr);
  gap: 12px;
}

.summary-item {
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7faff, #f3f7ff);
  border: 1px solid #e8eef8;
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.summary-item strong {
  word-break: break-all;
}

.summary-label {
  color: #8a94a6;
  font-size: 12px;
}

.settings-grid {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(320px, 1fr);
  gap: 18px;
}

.setting-form {
  max-width: 760px;
  position: relative;
}

.autofill-guard {
  position: absolute;
  width: 1px;
  height: 1px;
  left: -9999px;
  top: -9999px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.field-tip {
  color: #667085;
  font-size: 13px;
  line-height: 1.7;
}

.cookie-card :deep(.el-textarea__inner) {
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
}

.cookie-alert {
  margin-bottom: 14px;
}

.cookie-key-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 8px;
  color: #667085;
  font-size: 13px;
}

.cookie-key-row.second {
  margin-top: 18px;
}

@media (max-width: 1080px) {
  .settings-grid,
  .summary-strip {
    grid-template-columns: 1fr;
  }
}
</style>
